import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";

export function ResearchDialog({ ids, open, onOpenChange, onQueued }) {
  const { api } = useWorkspace();
  const [contacts, setContacts] = useState(true);
  const [est, setEst] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setEst(null); setErr(null);
    api.post("/stage1/research/estimate", { ids, include_contacts: contacts }).then(({ data }) => setEst(data)).catch((e) => setErr(errorMessage(e)));
  }, [api, ids, contacts, open]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stage1/research", { ids, include_contacts: contacts });
      toast.success(`Researching ${data.queued} companies — results appear as each one finishes`); onQueued(); onOpenChange(false);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="stage1-research-dialog">
        <DialogHeader>
          <DialogTitle>Research {ids.length} companies?</DialogTitle>
          <DialogDescription>For each company the AI finds its own website, checks it really is the type of business you want, and writes a short summary. Confirmed companies move into Companies with the evidence attached, and their current directors from Companies House are added as Contacts (free).</DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 rounded-md border p-3 text-sm"><input type="checkbox" checked={contacts} onChange={(e) => setContacts(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" data-testid="stage1-include-contacts" /><span>Also collect contacts from the company's website (named people and roles, public phone and email). These become Contacts linked to the company.</span></label>
        <div className="rounded-md bg-muted/50 p-3 text-sm" data-testid="stage1-estimate">
          {est ? <>{est.companies < ids.length && <p className="mb-1 text-xs text-muted-foreground">{ids.length - est.companies} already researched or in progress — skipped.</p>}
            <p>Estimated cost for {est.companies} companies: <b data-testid="stage1-estimate-typical">about £{est.typical_cost_gbp.toFixed(2)}</b> <span className="text-muted-foreground">(at most £{est.max_cost_gbp.toFixed(2)})</span></p>
            {est.balance_gbp != null && <p className="mt-1 text-xs text-muted-foreground" data-testid="stage1-estimate-balance">Your credit: £{est.balance_gbp.toFixed(2)}{est.balance_gbp < est.max_cost_gbp ? " — research pauses automatically if it runs out" : ""}</p>}
            {est.blocked_reason && <p className="mt-1 text-xs text-destructive" data-testid="stage1-estimate-blocked">{est.blocked_reason}</p>}</> : err ? <p className="text-destructive" data-testid="stage1-estimate-error">{err}</p> : <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="stage1-research-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy || !est?.companies || !!est?.blocked_reason} data-testid="stage1-research-confirm">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Research {est?.companies ?? ""} companies</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
